import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { WorkOrderChangeOtherPriceData } from 'gql/graphql';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';
import { TABLE_GROUP_NAME } from 'pages/Project/UerProject/WorkOrder/WorkOrderOthersTable/otherHelpers';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getWhereFromFilter } from '../helpers';
import { getWorkOrderChangeOtherPriceList } from '../WorkOrderChangeQueries';

const getGroupedOthers = (itemsArr: WorkOrderChangeOtherPriceData[] | undefined | null) => {
    if (!itemsArr) {
        return [];
    }

    const itemsByGroup = itemsArr?.reduce((acc: any, item: WorkOrderChangeOtherPriceData) => {
        const groupName = item.chapter || item.section || 'OTHERS';

        const itemWithType = { ...item, type: 'OTHERS' };

        if (acc[groupName]) {
            acc[groupName].push(itemWithType);
        } else {
            acc[groupName] = [{ id: groupName, name: TABLE_GROUP_NAME[groupName], isGroupTitle: true }, itemWithType];
        }

        return acc;
    }, {});

    return Object.values(itemsByGroup).reduce((acc: any[], group) => acc.concat(group), []);
};

export const useWorkOrderChangeOtherPriceList = ({
    workOrderChangeId,
    workOrderObjectId,
    workOrderChangeObjectId,
}: {
    workOrderChangeId: number;
    workOrderObjectId?: number | null;
    workOrderChangeObjectId: number | undefined;
}) => {
    const dispatch = useDispatch();

    const [filtersWhere, setFiltersWhere] = useState<Record<string, any> | undefined>(undefined);

    const variables = useMemo(
        () => ({
            workOrderChangeId,
            workOrderObjectId,
            workOrderChangeObjectId,
            take: 1000,
            skip: 0,
            where: filtersWhere,
        }),
        [workOrderChangeId, workOrderObjectId, workOrderChangeObjectId, filtersWhere],
    );
    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setFiltersWhere((prevValues) => ({
            and: {
                ...prevValues?.and,
                ...getWhereFromFilter(fieldName, checkedValues, null),
            },
        }));
    }, []);

    const { canSendRequest } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    changeFilterHandler,
                    `others_${workOrderObjectId}_${workOrderChangeObjectId}`,
                    getDefaultFilterCleaner({ changeFilterHandler, setFiltersWhere }),
                ),
            }),
            [changeFilterHandler, workOrderChangeObjectId, workOrderObjectId],
        ),
    );

    const [workOrderChangeOtherPriceData, , , workOrderChangeOtherPriceDataRequest] = useRequest({
        requestId: 'workOrderChangeOtherPrice',
        query: getWorkOrderChangeOtherPriceList,
        variables,
        withEffect: Boolean(canSendRequest && (workOrderObjectId || workOrderChangeObjectId)),
    });

    const handleGetWorkOrderChangeOtherPrice = useCallback(() => {
        dispatch(workOrderChangeOtherPriceDataRequest(variables));
    }, [dispatch, variables, workOrderChangeOtherPriceDataRequest]);

    const tableItems = useMemo(
        () => getGroupedOthers(workOrderChangeOtherPriceData?.uerWorkOrderChangeOtherPriceList?.items as any),
        [workOrderChangeOtherPriceData?.uerWorkOrderChangeOtherPriceList?.items],
    );

    const [resetData] = useResetStoreData({ requestId: 'workOrderChangeOtherPrice' });

    useEffect(
        () => () => {
            resetData(null);
        },
        [resetData],
    );

    const totalItems = useMemo(
        () => workOrderChangeOtherPriceData?.uerWorkOrderChangeOtherPriceList?.totalCount,
        [workOrderChangeOtherPriceData?.uerWorkOrderChangeOtherPriceList?.totalCount],
    );

    const fetchedItems = useMemo(
        () => workOrderChangeOtherPriceData?.uerWorkOrderChangeOtherPriceList?.items?.length,
        [workOrderChangeOtherPriceData?.uerWorkOrderChangeOtherPriceList?.items?.length],
    );

    return {
        tableItems,
        totalItems,
        fetchedItems,
        handleGetWorkOrderChangeOtherPrice,
        changeFilterHandler,
    };
};
